/**
 * Date arithmetic for the calendar, in the browser's own time zone. All-day
 * events are calendar days (stored as midnight UTC), so they are read with
 * UTC parts and never shift a day for a user east or west of Greenwich.
 */

import type { CalendarEvent } from 'zitimail-shared-types';

export type CalendarMode = 'month' | 'week' | 'day' | 'agenda';

export const DAY = 86_400_000;

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
}

/** Monday of the week containing `d`. */
export function startOfWeek(d: Date): Date {
  const day = startOfDay(d);
  return addDays(day, -((day.getDay() + 6) % 7));
}

/** The span a view shows: month as a six-week grid, agenda as the next 30 days. */
export function viewRange(mode: CalendarMode, anchor: Date): { start: Date; end: Date } {
  switch (mode) {
    case 'month': {
      const start = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
      return { start, end: addDays(start, 42) };
    }
    case 'week': {
      const start = startOfWeek(anchor);
      return { start, end: addDays(start, 7) };
    }
    case 'day': {
      const start = startOfDay(anchor);
      return { start, end: addDays(start, 1) };
    }
    case 'agenda': {
      const start = startOfDay(anchor);
      return { start, end: addDays(start, 30) };
    }
  }
}

export function step(mode: CalendarMode, anchor: Date, direction: 1 | -1): Date {
  switch (mode) {
    case 'month':
      return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
    case 'week':
      return addDays(anchor, 7 * direction);
    case 'day':
      return addDays(anchor, direction);
    case 'agenda':
      return addDays(anchor, 30 * direction);
  }
}

/** `YYYY-MM-DD` of a local date. */
export function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The local days an event covers, as `YYYY-MM-DD` keys. */
export function eventDays(event: CalendarEvent): string[] {
  const keys: string[] = [];
  if (event.allDay) {
    // Calendar days: walk the UTC dates, end exclusive.
    for (let t = Date.parse(event.start); t < Date.parse(event.end); t += DAY) {
      keys.push(new Date(t).toISOString().slice(0, 10));
    }
    return keys.length ? keys : [event.start.slice(0, 10)];
  }
  const start = new Date(event.start);
  const end = new Date(Math.max(Date.parse(event.end) - 1, start.getTime()));
  for (let d = startOfDay(start); d <= end; d = addDays(d, 1)) {
    keys.push(dayKey(d));
  }
  return keys;
}

export function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function title(mode: CalendarMode, anchor: Date): string {
  if (mode === 'month') {
    return anchor.toLocaleDateString([], { month: 'long', year: 'numeric' });
  }
  if (mode === 'day') {
    return anchor.toLocaleDateString([], {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  const { start, end } = viewRange(mode, anchor);
  const last = addDays(end, -1);
  return `${start.toLocaleDateString([], { day: 'numeric', month: 'short' })} – ${last.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/** Value for an `<input type="datetime-local">`. */
export function localInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dayKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
